import { Star } from "lucide-react";
import { Eyebrow, Marker } from "./Bits";
import Reveal from "@/components/Reveal";

/* Success Stories — Figma node 102:3258 (Frame 129).

   Row content is duplicated at render time so the marquee loop is seamless.

   Not a scaled stage — the rows animate continuously and are wider than the
   viewport by design. Instead every dimension steps up at xl to ~1.346× its Figma
   value, matching the scale the stage-based sections render at on wide screens. */

const QUOTE = {
  type: "quote",
  name: "Priya Sharma",
  role: "UI/UX Designer",
  avatar: "/images/hero-avatar-1.jpg",
  stars: 4,
  text: "“The structured learning paths and practical projects helped me build confidence beyond theory. I finally had a portfolio I was proud to share.”",
};
const CREATOR_A = {
  type: "creator",
  img: "/images/testimonial-creator.jpg",
  text: "Watch how Rohan turned content creation into a full-time career with GENRYZ.",
};
const CREATOR_B = {
  type: "creator",
  img: "/images/testimonial-creator-2.jpg",
  text: "Watch how Rohan turned content creation into a full-time career with GENRYZ.",
};
const LEARNER = { type: "learner", title: "From Student to UI/UX Designer" };

const ROW_1 = [QUOTE, CREATOR_A, LEARNER, QUOTE, LEARNER];
const ROW_2 = [QUOTE, LEARNER, QUOTE, CREATOR_B, LEARNER];

const shell =
  "shrink-0 overflow-hidden rounded-[17.767px] shadow-[0_0_20px_0_rgba(0,0,0,0.1)] xl:rounded-[24px]";

function QuoteCard({ data }) {
  return (
    <div className={`${shell} flex w-[320px] flex-col gap-[9px] bg-white px-7 py-[22px] sm:w-[389px] xl:w-[524px] xl:gap-3 xl:px-[39px] xl:py-[30px]`}>
      <div className="flex items-center gap-[15px] xl:gap-5">
        <span className="h-9 w-9 shrink-0 overflow-hidden rounded-full xl:h-12 xl:w-12">
          <img src={data.avatar} alt="" className="h-full w-full object-cover" loading="lazy" />
        </span>
        <span className="flex flex-col leading-[22px] xl:leading-[30px]">
          <span className="font-poppins text-[16px] text-black xl:text-[21px]">{data.name}</span>
          <span className="font-poppins text-[12px] text-[#8E8E8E] xl:text-[16px]">{data.role}</span>
        </span>
      </div>
      <div className="flex items-center gap-1 xl:gap-1.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <Star
            key={i}
            className={`h-5 w-5 xl:h-[27px] xl:w-[27px] ${i < data.stars ? "text-[#FFC807]" : "text-[#D9D9D9]"}`}
            fill="currentColor"
            strokeWidth={0}
          />
        ))}
      </div>
      <p className="font-poppins text-[12px] leading-[18px] text-black xl:text-[16px] xl:leading-[24px]">{data.text}</p>
    </div>
  );
}

function CreatorCard({ data }) {
  return (
    <div className={`${shell} relative flex h-[178px] w-[320px] flex-col justify-between bg-black px-[27px] py-[23px] sm:w-[370px] xl:h-[240px] xl:w-[498px] xl:px-[36px] xl:py-[31px]`}>
      <img src={data.img} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
      <span className="absolute inset-0 bg-black/25" aria-hidden />
      <span className="relative flex h-[30px] w-[110px] items-center justify-center rounded-[16px] bg-[var(--lime)] font-poppins text-[12px] text-black xl:h-[40px] xl:w-[148px] xl:rounded-[21px] xl:text-[16px]">
        Creator Story
      </span>
      <p className="relative font-poppins text-[14px] font-medium leading-5 text-white xl:text-[19px] xl:leading-[27px]">
        {data.text}
      </p>
    </div>
  );
}

function LearnerCard({ data }) {
  return (
    <div className={`${shell} flex w-[237px] flex-col gap-4 bg-white px-7 py-[22px] xl:w-[319px] xl:gap-5 xl:px-[39px] xl:py-[30px]`}>
      <span className="flex h-[33px] w-[106px] items-center justify-center rounded-[12.585px] bg-[#5C86C0] font-poppins text-[12px] font-medium text-white xl:h-[44px] xl:w-[143px] xl:rounded-[17px] xl:text-[16px]">
        Learner Story
      </span>
      <p className="font-poppins text-[22px] leading-[25px] text-black xl:text-[30px] xl:leading-[34px]">{data.title}</p>
      <span className="font-poppins text-[12px] leading-4 text-[#5C86C0] xl:text-[16px]">Watch Story →</span>
    </div>
  );
}

function Card({ data }) {
  if (data.type === "creator") return <CreatorCard data={data} />;
  if (data.type === "learner") return <LearnerCard data={data} />;
  return <QuoteCard data={data} />;
}

export default function TestimonialsSection() {
  return (
    <section className="overflow-hidden py-12 lg:py-8 xl:py-10">
      <style>{`
        @keyframes tmLeft  { 0%{transform:translateX(0)}   100%{transform:translateX(-50%)} }
        @keyframes tmRight { 0%{transform:translateX(-50%)} 100%{transform:translateX(0)} }
        .tm-row { animation: tmLeft 60s linear infinite; }
        .tm-row.reverse { animation-name: tmRight; }
        .tm-track:hover .tm-row { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce){ .tm-row { animation: none } }
      `}</style>

      <div className="mx-auto w-full max-w-[1680px] px-5 sm:px-6">
        {/* Below sm the copy centres — from sm up the original left alignment. */}
        <div className="flex flex-col-reverse items-center gap-5 text-center sm:items-start sm:text-left lg:flex-row lg:items-start lg:justify-between lg:gap-6">
          <Reveal className="min-w-0">
            <h2 className="font-poppins text-[30px] font-medium leading-[1.65] text-black sm:text-[38px] lg:text-[44px] xl:text-[56px]">
              Success Stories That<Marker color="#8F8F8F" className="text-white">Inspire.</Marker>
            </h2>
            {/* Capped tighter than the scaled Figma width so the line breaks land
                in the same places. */}
            <p className="mx-auto mt-4 max-w-[560px] font-poppins text-[16px] font-light leading-[1.5] text-black sm:mx-0 lg:text-[17px] xl:max-w-[640px] xl:text-[21px]">
              Discover how we are helping learners, creators, and professionals build skills, grow their careers, and unlock new opportunities.
            </p>
          </Reveal>
          <Reveal delay={80} className="shrink-0 lg:pt-8">
            <Eyebrow height={60} className="min-w-[175px] xl:min-w-[235px]">Testimonials</Eyebrow>
          </Reveal>
        </div>
      </div>

      <div className="mt-10 flex flex-col gap-4 sm:gap-5 xl:mt-[80px] xl:gap-[38px]">
        {[ROW_1, ROW_2].map((row, ri) => (
          <div key={ri} className="tm-track overflow-hidden py-2">
            <div className={`tm-row flex w-max items-stretch gap-4 sm:gap-5 xl:gap-7 ${ri === 1 ? "reverse" : ""}`}>
              {[...row, ...row].map((c, i) => (
                <Card key={`${c.type}-${i}`} data={c} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Results disclaimer — not in the Figma frame, carried over from the previous
          landing page. Kept deliberately: it guards outcome claims made by the
          testimonials. Remove only as a considered decision. */}
      <p className="mx-auto mt-10 max-w-[1680px] px-5 text-center font-poppins text-[12px] text-[#8E8E8E]">
        Individual results. Outcomes depend on time, effort, and consistency — not promises.
      </p>
    </section>
  );
}
